import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import withRouter from '../Components/Common/withRouter';
import { useLocation } from 'react-router-dom';

//import Components
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import RightSidebar from '../Components/Common/RightSidebar';
import FiltersBar from '../Components/Common/FiltersBar';
import { FiltersProvider, useFilters } from '../Context/FiltersContext';

//import actions
import {
	changeLayout,
	changeSidebarTheme,
	changeLayoutMode,
	changeLayoutWidth,
	changeLayoutPosition,
	changeTopbarTheme,
	changeLeftsidebarSizeType,
	changeLeftsidebarViewType,
	changeSidebarImageType,
	changeSidebarVisibility,
	baseSettings,
} from '../slices/thunks';

//redux
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { darkenColor, hexToRgb } from '../Components/Hooks/useColorArrayHooks';
import hexToRgba from '../Components/Hooks/HexToRGBa';
import { ToastContainer } from 'react-toastify';

const Layout = props => {
	const shouldHideMainMenu = props.shouldHideMainMenu;
	const dispatch = useDispatch();
	const location = useLocation();

	const shouldShowFilters = useMemo(() => {
		const dashboardRoutes = [
			'/environment/variable',
			'/monitoring-fan/dashboard', 
			'/RILes/dashboard'
		];
		return dashboardRoutes.some(route => location.pathname.includes(route));
	}, [location.pathname]);

	// Memoized selector to prevent unnecessary recalculations
	const selectLayoutProperties = useMemo(
		() =>
			createSelector(
				state => state.Layout,
				layout => ({
					layoutType: layout.layoutType,
					leftSidebarType: layout.leftSidebarType,
					layoutModeType: layout.layoutModeType,
					layoutWidthType: layout.layoutWidthType,
					layoutPositionType: layout.layoutPositionType,
					topbarThemeType: layout.topbarThemeType,
					leftsidbarSizeType: layout.leftsidbarSizeType,
					leftSidebarViewType: layout.leftSidebarViewType,
					leftSidebarImageType: layout.leftSidebarImageType,
					preloader: layout.preloader,
					sidebarVisibilitytype: layout.sidebarVisibilitytype,
				})
			),
		[]
	);

	const layoutProperties = useSelector(selectLayoutProperties);

	const {
		layoutType,
		leftSidebarType,
		layoutModeType,
		layoutWidthType,
		layoutPositionType,
		topbarThemeType,
		leftsidbarSizeType,
		leftSidebarViewType,
		leftSidebarImageType,
		preloader,
		sidebarVisibilitytype,
	} = layoutProperties;

	useEffect(() => {
		if (
			layoutType ||
			leftSidebarType ||
			layoutModeType ||
			layoutWidthType ||
			layoutPositionType ||
			topbarThemeType ||
			leftsidbarSizeType ||
			leftSidebarViewType ||
			leftSidebarImageType ||
			sidebarVisibilitytype
		) {
			window.dispatchEvent(new Event('resize'));
			dispatch(changeLeftsidebarViewType(leftSidebarViewType));
			dispatch(changeLeftsidebarSizeType(leftsidbarSizeType));
			dispatch(changeSidebarTheme(leftSidebarType));
			dispatch(changeLayoutMode(layoutModeType));
			dispatch(changeLayoutWidth(layoutWidthType));
			dispatch(changeLayoutPosition(layoutPositionType));
			dispatch(changeTopbarTheme(topbarThemeType));
			dispatch(changeLayout(layoutType));
			dispatch(changeSidebarImageType(leftSidebarImageType));
			dispatch(changeSidebarVisibility(sidebarVisibilitytype));
		}
	}, [
		layoutType,
		leftSidebarType,
		layoutModeType,
		layoutWidthType,
		layoutPositionType,
		topbarThemeType,
		leftsidbarSizeType,
		leftSidebarViewType,
		leftSidebarImageType,
		sidebarVisibilitytype,
		dispatch,
	]);

	const onChangeLayoutMode = value => {
		if (changeLayoutMode) {
			dispatch(changeLayoutMode(value));
		}
	};

	const [headerClass, setHeaderClass] = useState('');

	useEffect(() => {
		window.addEventListener('scroll', scrollNavigation, true);
		return () => {
			window.removeEventListener('scroll', scrollNavigation, true);
		};
	});

	const scrollNavigation = () => {
		var scrollup = document.documentElement.scrollTop;
		if (scrollup > 50) {
			setHeaderClass('topbar-shadow');
		} else {
			setHeaderClass('');
		}
	};

	
	useEffect(() => {
		const hamburger = document.querySelector('.hamburger-icon');
		if (!hamburger) return;

		if (shouldHideMainMenu) {
			hamburger.classList.remove('open');
			return;
		}

		if (
			sidebarVisibilitytype === 'show' ||
			layoutType === 'vertical' ||
			layoutType === 'twocolumn'
		) {
			hamburger.classList.remove('open');
		} else {
			hamburger.classList.add('open');
		}
	}, [sidebarVisibilitytype, layoutType, shouldHideMainMenu]);

	const FiltersBarContainer = () => {
		const {
			showFilters,
			isLoadingEstablishments,
			filterOptions,
			selectedRegions,
			selectedBarrios,
			selectedCentros,
			selectedEmpresas,
			selectedAreas,
			setSelectedRegions,
			setSelectedBarrios,
			setSelectedCentros,
			setSelectedEmpresas,
			setSelectedAreas,
			filteredEstablishments,
			clearAllFilters
		} = useFilters();

		return (
			<FiltersBar
				showFilters={showFilters}
				isLoading={isLoadingEstablishments}
				filterOptions={filterOptions}
				selectedRegions={selectedRegions}
				selectedBarrios={selectedBarrios}
				selectedCentros={selectedCentros}
				selectedEmpresas={selectedEmpresas}
				selectedAreas={selectedAreas}
				setSelectedRegions={setSelectedRegions}
				setSelectedBarrios={setSelectedBarrios}
				setSelectedCentros={setSelectedCentros}
				setSelectedEmpresas={setSelectedEmpresas}
				setSelectedAreas={setSelectedAreas}
				filteredCount={filteredEstablishments.length}
				onClearFilters={clearAllFilters}
			/>
		);
	};

	return (
		<React.Fragment>
			<div id="layout-wrapper">
				<FiltersProvider>

					{!shouldHideMainMenu && (
						<Header
							headerClass={headerClass}
							layoutModeType={layoutModeType}
							onChangeLayoutMode={onChangeLayoutMode}
						/>
					)}

					<ToastContainer position="top-right" />
					
					{!shouldHideMainMenu && <Sidebar layoutType={layoutType} />}

					{!shouldHideMainMenu && shouldShowFilters && <FiltersBarContainer />}

					<div className="main-content">
						{props.children}
						
						{!shouldHideMainMenu && <Footer />}
					</div>
				</FiltersProvider>
			</div>
		</React.Fragment>
	);
};

Layout.propTypes = {
	children: PropTypes.object,
};

export default withRouter(Layout);
